/** @jsxImportSource @emotion/react */
import { useApolloClient } from '@apollo/client'
import Typography from '@mui/material/Typography'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { TAKE_ALL_ITEMS_COUNT } from 'constants/consts'
import { SubjectEnumType } from '__generatedTypes__/globalTypes'
import {
  findAllQuestionPassageBank,
  findAllQuestionPassageBank_findAllQuestionPassageBankCMS_items as QuestionPassageBank,
  findAllQuestionPassageBankVariables,
} from '__generatedTypes__/findAllQuestionPassageBank'
import {
  updateQuestionPassageBank,
  updateQuestionPassageBankVariables,
} from '__generatedTypes__/updateQuestionPassageBank'
import { SubjectTabs } from 'components/SubjectsTabs'
import { usePlatformsAndSubjects, Props as UsePlatformsAndSubjects } from 'hooks/usePlatformsAndSubjects'
import { GET_LIST_QUESTION_PASSAGE_BANK, UPDATE_QUESTION_PASSAGE_BANK } from 'queries/questionPassageBank'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { PageLoader } from 'components/PageLoader'
import { toast } from 'utils/toast'
import { DescriptionForm } from 'pagesNew/QuestionBanks/List/DescriptionForm'
import * as classes from './styles'
import { QuestionPassageBanksTable } from './components/QuestionPassageBanksTable'

export const List = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const subjectId = searchParams.get('subjectId')
  const { navigate } = useLink()
  const { query, mutate } = useApolloClient()
  const [questionPassageBanks, setQuestionPassageBanks] = useState<QuestionPassageBank[]>([])
  const [submittingQuestionPassageBankId, setSubmittingQuestionPassageBankId] = useState<string | undefined>()
  const [isLoaded, setIsLoaded] = useState(false)
  const platformsAndSubjectsProps: UsePlatformsAndSubjects = useMemo(
    () => ({
      initialSelectedSubjectId: subjectId ?? undefined,
      filterSubjects: (subject) => subject.type === SubjectEnumType.Game,
    }),
    [subjectId]
  )
  const { subjects, selectedSubject, selectSubject } = usePlatformsAndSubjects(platformsAndSubjectsProps)

  const loadQuestionPassageBanks = useCallback(async () => {
    if (!selectedSubject) {
      return
    }

    setSearchParams((prev) => ({ ...Object.fromEntries(prev.entries()), subjectId: selectedSubject.id }))
    const { data } = await query<findAllQuestionPassageBank, findAllQuestionPassageBankVariables>({
      query: GET_LIST_QUESTION_PASSAGE_BANK,
      variables: {
        params: {
          take: TAKE_ALL_ITEMS_COUNT,
          where: {
            subject: {
              id: selectedSubject.id,
            },
          },
        },
      },
      fetchPolicy: 'network-only',
    })
    setQuestionPassageBanks(data.findAllQuestionPassageBankCMS.items ?? [])
    setIsLoaded(true)
  }, [selectedSubject])

  useEffect(() => {
    loadQuestionPassageBanks()
  }, [loadQuestionPassageBanks])

  const handleIsLiveChange = useCallback(
    async (questionPassageBank: QuestionPassageBank, isLiveUpd: boolean) => {
      setSubmittingQuestionPassageBankId(questionPassageBank.id)
      setQuestionPassageBanks((prev) =>
        prev.map((currentQuestionPassageBank) => {
          if (questionPassageBank.id !== currentQuestionPassageBank.id) {
            return currentQuestionPassageBank
          }

          return {
            ...currentQuestionPassageBank,
            isLive: isLiveUpd,
          }
        })
      )

      try {
        await mutate<updateQuestionPassageBank, updateQuestionPassageBankVariables>({
          mutation: UPDATE_QUESTION_PASSAGE_BANK,
          variables: {
            params: {
              id: questionPassageBank.id,
              isLive: isLiveUpd,
            },
          },
        })
        toast.success('CARS Question Bank updated successfully')
      } catch (e) {
        toast.error('Failed to update CARS Question Bank')
        await loadQuestionPassageBanks()
      }

      setSubmittingQuestionPassageBankId(undefined)
    },
    [loadQuestionPassageBanks]
  )

  const handleAddClick = useCallback(() => {
    if (!selectedSubject) {
      return
    }

    navigate(cmsRoutes.CARS_QUESTION_BANK_CREATE, undefined, {
      searchParams: new URLSearchParams({
        subjectId: selectedSubject.id,
      }),
    })
  }, [selectedSubject, questionPassageBanks.length])

  if (!isLoaded || !selectedSubject) {
    return <PageLoader />
  }

  return (
    <div css={classes.wrapper}>
      <Typography variant="title3" css={classes.title}>
        CARS Question Banks
      </Typography>
      <SubjectTabs subjects={subjects} selectedSubject={selectedSubject} selectSubject={selectSubject} />
      <DescriptionForm subjectId={selectedSubject.id} tabIndex={selectedSubject.tabIndex} />
      <div css={classes.space} />
      <QuestionPassageBanksTable
        gameSlug={selectedSubject.slug}
        questionPassageBanks={questionPassageBanks}
        submittingQuestionPassageBankId={submittingQuestionPassageBankId}
        onAddClick={handleAddClick}
        onIsLiveChange={handleIsLiveChange}
        disableTableButtons={!selectedSubject}
      />
    </div>
  )
}
